/* =====================================================================================
   Scira's own worked examples: contracts and receivables.

   WHY THEY MOVED HERE, which is a security fix rather than a tidy-up.

   These lived inline in Operations.tsx and Ledger.tsx behind a runtime gate, so the DATA was
   compiled into every build and only hidden at render time. That included a real consultancy
   agreement naming a real counterparty and a real opportunity at a named hospital.

   The demonstration builds appeared clean, and were, but only by accident: with @tenant aliased
   at a concrete module the bundler could prove the gate was false and fold the arrays away.
   The showcase build resolves @tenant at runtime, the bundler can prove nothing, and Scira's
   CareDx contract turned up in a bundle meant to be sent to strangers. The isolation check
   caught it before it shipped.

   Data that belongs to one tenant belongs in that tenant's seed, where the build-time alias
   keeps it out of every other bundle by construction rather than by optimiser behaviour.
   ===================================================================================== */

export const SCIRA_CONTRACTS = [
  {
    ref: 'CTR-00003', title: 'CareDx consultancy agreement', party: 'CareDx, Inc.',
    type: 'consultancy', status: 'expired', signed: '2023-07-22', expires: '2026-07-22',
    autoRenew: false, noticeDays: null, value: null, currency: 'GBP',
    flags: ['non_compete'], risk: 'high',
    note:
      'Ended 22 July 2026. The Material Interest non-compete runs six months from termination, so it '
      + 'clears on 22 January 2027, not twelve months as originally assumed. Commercial activity in '
      + 'the restricted scope should be sequenced after that date.',
  },
  {
    ref: 'CTR-00007', title: 'IP licence, Patient Thread to Scira', party: 'Patient Thread Limited',
    type: 'licence_in', status: 'active', signed: '2026-07-14', expires: null,
    autoRenew: false, noticeDays: null, value: null, currency: 'GBP',
    flags: ['exclusivity', 'ip_assignment'], risk: 'critical',
    note:
      'Field-of-use exclusive licence of the diagnostic portfolio. Must be at arm\'s length for SEIS '
      + 'purposes, and the IP staying with Patient Thread is what keeps it off Scira\'s balance sheet '
      + 'for the gross assets test.',
  },
  {
    ref: 'CTR-00011', title: 'Distribution agreement, Iberia', party: 'Iberia Diagnostica SL',
    type: 'distribution', status: 'active', signed: '2025-07-01', expires: '2028-06-30',
    autoRenew: true, noticeDays: 90, value: 250_000, currency: 'EUR',
    flags: ['exclusivity', 'min_commitment'], risk: 'medium',
    note: 'Auto-renews unless notice served. Minimum commitment breach is the trigger to convert exclusivity.',
  },
  {
    ref: 'CTR-00014', title: 'Data processing agreement, Patient Thread to Scira', party: 'Patient Thread Limited',
    type: 'other', status: 'draft', signed: null, expires: null,
    autoRenew: false, noticeDays: null, value: null, currency: 'GBP',
    flags: ['data_protection'], risk: 'critical',
    note:
      'Article 28 UK GDPR requires this in writing before processing begins. There is no exemption '
      + 'for companies under common control. UNSIGNED and currently blocking go-live.',
  },
];

export const SCIRA_INVOICES = [
  { id: 'i1', number: 'INV-00021', account: 'AOU Consorziale Policlinico di Bari', country: 'ITA',
    issued: '2026-07-20', due: '2026-09-18', currency: 'EUR', total: 18_400, paid: 0,
    status: 'sent', dunningStage: 0, postedToSage: true },
  { id: 'i2', number: 'INV-00019', account: 'Iberia Diagnostica SL', country: 'ESP',
    issued: '2026-06-02', due: '2026-07-02', currency: 'EUR', total: 42_000, paid: 42_000,
    status: 'paid', dunningStage: 0, postedToSage: true },
  { id: 'i3', number: 'INV-00017', account: 'Nordisk Diagnostik AB', country: 'SWE',
    issued: '2026-05-15', due: '2026-06-14', currency: 'EUR', total: 31_500, paid: 15_000,
    status: 'part_paid', dunningStage: 2, lastChased: '2026-07-14',
    promise: { amount: 16_500, date: '2026-08-15', by: 'Finance director', status: 'open' },
    postedToSage: true },
  { id: 'i4', number: 'INV-00014', account: 'Azienda Ospedale-Università Padova', country: 'ITA',
    issued: '2026-04-10', due: '2026-06-09', currency: 'EUR', total: 12_800, paid: 0,
    status: 'disputed', disputed: true,
    disputeReason: 'Purchase order number missing from the invoice. Italian public bodies will not pay without it.',
    dunningStage: 1, lastChased: '2026-06-20', postedToSage: true },
  { id: 'i5', number: 'INV-00011', account: 'Royal Free London NHS Foundation Trust', country: 'GBR',
    issued: '2026-03-01', due: '2026-03-31', currency: 'GBP', total: 8_900, paid: 0,
    status: 'overdue', dunningStage: 4, lastChased: '2026-07-25',
    promise: { amount: 8_900, date: '2026-07-15', by: 'Lab manager', status: 'broken' },
    postedToSage: true },
  { id: 'i6', number: 'INV-00022', account: 'Ospedale Civile S. Salvatore', country: 'ITA',
    issued: '2026-07-30', due: '2026-09-28', currency: 'EUR', total: 6_200, paid: 0,
    status: 'issued', dunningStage: 0, postedToSage: false },
];
